import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { Cars, Currency, FPError } from 'types';
import { CarService } from 'api';
import { Subscription, throwError } from 'rxjs';
import { mapError } from '../../utils';
import router from '../../router';
import { finalize, timeoutWith } from 'rxjs/operators';
import { Events } from './../../analytics';

// import {get as getCookie, set as setCookie} from 'es-cookie';
// import i18n from '../../plugins/i18n';

let searchSubscription: Subscription = null;
let performSearchSubscription: Subscription = null;
let offerSubscription: Subscription = null;

export class State {
  loading: boolean = false;
  results: Cars.Search = null;
  offer: Cars.CarDetails = null;
  filterIsOpened: boolean = false;
  searchCriteria: Cars.SearchCriteria = Cars.SearchCriteria.empty();
  query: string = '';
  error: FPError = null;
  isRedirectModalShown: boolean = false;
}

const getters = <GetterTree<State, any>>{
  getCars(state: State): Cars.CarDetails[] {
    if (state.results) {
      return state.results.filtered(state.query);
    } else {
      return [];
    }
  },
  getFilter(state: State): Cars.FilterOptions {
    if (state.results && state.results.cars && state.results.cars.length > 0) {
      return state.results.filter;
    } else {
      return null;
    }
  },
  getErrorMessage(state: State): string {
    return state.error && state.error.message ? state.error.message : '';
  },
};

const mutations = <MutationTree<State>>{
  setPickUpLocationId(state: State, locationId: string) {
    state.searchCriteria.pickUpId = locationId;
  },
  setDropOffLocationId(state: State, locationId: string) {
    state.searchCriteria.dropOffId = locationId;
  },
  setPickUpLocationName(state: State, locationName: string) {
    state.searchCriteria.pickUpLocation = locationName;
  },
  setDropOffLocationName(state: State, locationName: string) {
    state.searchCriteria.dropOffLocation = locationName;
  },
  setPickUpDate(state: State, date: Date) {
    state.searchCriteria.pickUpDate = date;
  },
  setPickUpTime(state: State, time: string) {
    state.searchCriteria.pickUpTime = time;
  },
  setDropOffDate(state: State, date: Date) {
    state.searchCriteria.dropOffDate = date;
  },
  setDropOffTime(state: State, time: string) {
    state.searchCriteria.dropOffTime = time;
  },
  setResults(state: State, results: Cars.SearchResults) {
    state.results = new Cars.Search(results, window.location.href, new Date().getTime());
  },
  dropResults(state: State) {
    state.results = null;
  },
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  setError(state: State, error: FPError) {
    state.error = error ? mapError(error) : null;
  },
  setSearchCriteria(state: State, criteria: Cars.SearchCriteria) {
    state.searchCriteria = criteria;
  },
  showMoreFilters(state: State, show: boolean) {
    state.filterIsOpened = show;
  },
  setQuery(state: State, query: string) {
    state.query = query.trim();
  },
  applyFilter(state: State, filter: Cars.FilterOptions) {
    if (!filter) {
      return;
    }

    if (!state.results || !state.results.filter) {
      return;
    }

    if (state.results.filter.priceRange) {
      state.results.filter.priceRange.right = filter.priceRange.right;
      state.results.filter.priceRange.left = filter.priceRange.left;
    }

    if (state.results.filter.freeCancellation) {
      state.results.filter.freeCancellation.selected = filter.freeCancellation.selected;
    }
    if (state.results.filter.postPay) {
      state.results.filter.postPay.selected = filter.postPay.selected;
    }
    state.results.filter.rating = filter.rating;
    state.results.filter.type = filter.type;
    state.results.filter.seats = filter.seats;
    state.results.filter.vendors = filter.vendors;
    state.results.filter.transmissions = filter.transmissions;
    state.results.filter.fuelType = filter.fuelType;
  },
  setPriceRange(state: State, { left, right }: { left: number; right: number }) {
    state.results.filter.priceRange.right = right;
    state.results.filter.priceRange.left = left;
  },
  setCarType(state: State, index: number) {
    state.results.filter.type.forEach((it, i) => {
      i == index ? it.toggle() : (it.selected = false);
    });
  },
  setRating(state: State, index: number) {
    state.results.filter.rating.forEach((it, i) => {
      i == index ? it.toggle() : (it.selected = false);
    });
  },
  setFreeCancellation(state: State) {
    state.results.filter.freeCancellation.toggle();
  },
  setPostPay(state: State) {
    state.results.filter.postPay.toggle();
  },
  resetFilters(state: State) {
    state.results.resetFilters();
  },
  setOffer(state: State, offer?: Cars.CarDetails) {
    state.offer = offer;
  },
  toggleRedirectModal(state: State, show: boolean) {
    state.isRedirectModalShown = show;
  }
};

const actions = <ActionTree<State, any>>{
  runCarsSearch(store: ActionContext<State, any>) {
    store.commit('setError', null);
    store.dispatch('unsubscribeSearch');
    store.commit('setLoading', true);
    store.commit('dropResults');
    store.dispatch('support/setRefreshDialogShow', { show: false, messgage: null }, { root: true });
    store.dispatch('support/stopCountdown', {}, { root: true });

    const CURRENCY = store.rootState.support.currency as Currency.CurrencyItem;

    const requestBody: Cars.SearchRequestBody = {
      name: 'search-car',
      payload: store.state.searchCriteria.toRequestBody(CURRENCY.currency),
    };

    Events.Car.Search.start();
    searchSubscription = CarService.asyncSearch(requestBody).subscribe(
      (res) => {
        store.dispatch('performSearch', res.job_id);
      },
      (err) => {
        store.commit('setError', err);
        store.commit('setLoading', false);
      }
    );
  },

  performSearch(store: ActionContext<State, any>, jobId: string) {
    store.commit('setError', null);
    performSearchSubscription = CarService.performSearchByJobId(jobId)
      .pipe(timeoutWith(3 * 60 * 1000, throwError(new Error('timeout'))))
      .pipe(
        finalize(() => {
          Events.Car.Search.finish();
        })
      )
      .subscribe(
        async (res) => {
          if (res.job_status === 'completed') {
            performSearchSubscription.unsubscribe();
            store.commit('setLoading', false);
            if (!res.result.error) {
              store.commit('setResults', res.result);
              store.dispatch('support/initResfreshCountdown', {
                time: 29 * 60 * 1000
              }, {
                root: true
              });
            }
          }
        },
        (err) => {
          performSearchSubscription.unsubscribe();
          store.commit('setError', err);
          store.commit('setLoading', false);
        }
      );
  },

  fetchOffer(store: ActionContext<State, any>) {
    if (!store.state.searchCriteria.offerId) {
      return;
    }

    store.commit('setError', null);
    store.dispatch('unsubscribeOffer');

    store.commit('setLoading', true);
    store.commit('setOffer', null);

    offerSubscription = CarService.getOffer(store.state.searchCriteria.offerId).subscribe(
      (res) => {
        store.commit('setLoading', false);
        const offer = Cars.CarDetails.offerFactory(res);
        store.commit('setOffer', offer);
      },
      (err) => {
        store.commit('setError', mapError(err));
        store.commit('setLoading', false);
      }
    );
  },

  unsubscribeSearch(store: ActionContext<State, any>) {
    searchSubscription?.unsubscribe();
    performSearchSubscription?.unsubscribe();
  },

  unsubscribeOffer(store: ActionContext<State, any>) {
    offerSubscription?.unsubscribe();
  },

  changeOffer(store: ActionContext<State, any>, update?: boolean) {
    if (store.state.results) {
      router.back();
      if (update) {
        store.dispatch('runCarsSearch');
      }
    } else {
      store.dispatch('pushToSearch');
    }
  },

  pushToSearch(store: ActionContext<State, any>) {
    const query = store.state.searchCriteria.toQuery();
    router.push({ path: `/cars/search/`, query: query }).catch((e) => {
      console.log(e);
    });
  },
};

export const car = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
